import type { SegmentConditionGroups } from '@reach/shared-types/conditions';
import { generateId } from '../utils/generate-id';
import React, { useEffect, useRef, useState } from 'react';
import { ConditionGroupList } from './ConditionGroupList';
import { useSegmentSchemaCatalog } from './SegmentSchemaCatalog';
import { builderStateToConditionGroups, conditionGroupsToBuilderState } from './segment-builder.utils';
import { isSegmentDefinitionComplete } from './segment-validation';
import type { BuilderState } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';
import { SegmentPortalContext } from './segment-portal-context';
import { SegmentReadOnlyContext } from './segment-read-only-context';

interface SegmentVisualBuilderProps {
  className?: string;
  label?: string;
  portalContainer?: HTMLElement | null;
  disabled?: boolean;
  readOnly?: boolean;
  onValidationChange?: (valid: boolean) => void;
  conditionGroups: SegmentConditionGroups | null;
  onChange: (groups: SegmentConditionGroups) => void;
  onUnsupported: (reason: string) => void;
}

export const SegmentVisualBuilder: React.FC<SegmentVisualBuilderProps> = ({
  conditionGroups,
  className = '',
  label = 'Segment conditions',
  portalContainer,
  disabled = false,
  readOnly = false,
  onValidationChange,
  onChange,
  onUnsupported,
}) => {
  const { schemaCatalog, isLoading, isFetching, error, retry } = useSegmentSchemaCatalog();
  const [builderState, setBuilderState] = useState<BuilderState>(() => ({
    groups: [],
  }));
  const lastEmittedGroupsRef = useRef<string | null>(null);

  useEffect(() => {
    // A refreshed catalog may change whether stored relationships are available.
    lastEmittedGroupsRef.current = null;
  }, [schemaCatalog]);

  useEffect(() => {
    if (isLoading || !schemaCatalog) return;
    const groups = conditionGroups ?? [];
    const groupsKey = JSON.stringify(groups);
    if (groupsKey === lastEmittedGroupsRef.current) return;
    lastEmittedGroupsRef.current = groupsKey;
    const conversionResult = conditionGroupsToBuilderState(groups, schemaCatalog);
    if (conversionResult.kind === 'unsupported') {
      onUnsupported(conversionResult.reason);
      return;
    }
    const state = conversionResult.state;
    if (state.groups.length === 0) {
      state.groups = [
        {
          id: generateId(),
          logic: 'and',
          conditions: [],
          isExpanded: true,
        },
      ];
    }
    setBuilderState(state);
  }, [schemaCatalog, conditionGroups, isLoading, onUnsupported]);

  const catalogIsReady = !isLoading && !error && schemaCatalog !== undefined;
  const valid = catalogIsReady && isSegmentDefinitionComplete(conditionGroups ?? [], schemaCatalog);
  useEffect(() => {
    onValidationChange?.(valid);
  }, [valid, onValidationChange]);

  const handleUpdate = (nextState: BuilderState) => {
    if (disabled || readOnly) return;
    setBuilderState(nextState);
    const groups = builderStateToConditionGroups(nextState);
    lastEmittedGroupsRef.current = JSON.stringify(groups);
    onChange(groups);
  };

  if (error) {
    return (
      <div className={styles.emptyBuilder}>
        <p>Couldn’t load fields. Please try again.</p>
        <button type="button" className={styles.addConditionBtn} onClick={retry} disabled={isFetching}>
          Retry
        </button>
      </div>
    );
  }

  if (isLoading || !schemaCatalog) {
    return (
      <div className={styles.emptyBuilder}>
        <p>Loading fields...</p>
      </div>
    );
  }

  const rootClassName = `${styles.editorRoot} ${className}`;
  return (
    <SegmentPortalContext.Provider value={portalContainer}>
      <SegmentReadOnlyContext.Provider value={readOnly}>
        <fieldset className={rootClassName} disabled={disabled || readOnly}>
          <legend>{label}</legend>
          <ConditionGroupList
            state={builderState}
            onUpdate={handleUpdate}
            disabled={disabled || readOnly || isLoading}
          />
        </fieldset>
      </SegmentReadOnlyContext.Provider>
    </SegmentPortalContext.Provider>
  );
};
